import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  where,
  onSnapshot,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Collections
const DEBTORS_COLLECTION = 'debtors';
const SETTINGS_COLLECTION = 'settings';
const USERS_COLLECTION = 'users';
const SITE_CONFIG_DOC = 'siteConfig';

// ============ SITE CONFIG ============

export const getSiteConfig = async () => {
  const docSnap = await getDoc(doc(db, 'config', SITE_CONFIG_DOC));
  if (docSnap.exists()) {
    return docSnap.data();
  }
  return null;
};

export const subscribeToSiteConfig = (callback) => {
  return onSnapshot(doc(db, 'config', SITE_CONFIG_DOC), (docSnap) => {
    if (docSnap.exists()) {
      callback(docSnap.data());
    } else {
      callback(null);
    }
  });
};

export const updateSiteConfig = async (config) => {
  await setDoc(doc(db, 'config', SITE_CONFIG_DOC), {
    ...config,
    updatedAt: serverTimestamp()
  }, { merge: true });
};

export const initSiteConfig = async () => {
  const existing = await getSiteConfig();
  if (!existing) {
    const defaultConfig = {
      siteTitle: 'BERKAY ÇAKIR',
      siteSubtitle: 'Hukuk Bürosu',
      primaryColor: '#f59e0b',
      logoLight: '',
      logoDark: '',
      loginImage: '',
      favicon: '',
      createdAt: serverTimestamp()
    };
    await setDoc(doc(db, 'config', SITE_CONFIG_DOC), defaultConfig);
    return defaultConfig;
  }
  return existing;
};

// ============ DEBTORS ============

export const getDebtors = async () => {
  const querySnapshot = await getDocs(
    query(collection(db, DEBTORS_COLLECTION), orderBy('createdAt', 'desc'))
  );
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const subscribeToDebtors = (callback) => {
  const q = query(collection(db, DEBTORS_COLLECTION), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const debtors = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(debtors);
  });
};

export const addDebtor = async (debtorData) => {
  const docRef = await addDoc(collection(db, DEBTORS_COLLECTION), {
    ...debtorData,
    createdAt: serverTimestamp()
  });
  return { id: docRef.id, ...debtorData };
};

export const updateDebtor = async (debtorId, updates) => {
  const debtorRef = doc(db, DEBTORS_COLLECTION, debtorId);
  await updateDoc(debtorRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

export const deleteDebtor = async (debtorId) => {
  await deleteDoc(doc(db, DEBTORS_COLLECTION, debtorId));
};

export const getDebtorById = async (debtorId) => {
  const docSnap = await getDoc(doc(db, DEBTORS_COLLECTION, debtorId));
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
};

// ============ SETTINGS ============

export const getSettings = async () => {
  const querySnapshot = await getDocs(collection(db, SETTINGS_COLLECTION));
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const subscribeToSettings = (callback) => {
  return onSnapshot(collection(db, SETTINGS_COLLECTION), (snapshot) => {
    const settings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(settings);
  });
};

export const addSetting = async (settingData) => {
  const docRef = await addDoc(collection(db, SETTINGS_COLLECTION), settingData);
  return { id: docRef.id, ...settingData };
};

export const updateSetting = async (settingId, updates) => {
  const settingRef = doc(db, SETTINGS_COLLECTION, settingId);
  await updateDoc(settingRef, updates);
};

export const deleteSetting = async (settingId) => {
  await deleteDoc(doc(db, SETTINGS_COLLECTION, settingId));
};

// ============ USERS ============

export const getUsers = async () => {
  const querySnapshot = await getDocs(collection(db, USERS_COLLECTION));
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const subscribeToUsers = (callback) => {
  return onSnapshot(collection(db, USERS_COLLECTION), (snapshot) => {
    const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(users);
  });
};

export const updateUser = async (userId, updates) => {
  const userRef = doc(db, USERS_COLLECTION, userId);
  await updateDoc(userRef, updates);
};

export const deleteUser = async (userId) => {
  await deleteDoc(doc(db, USERS_COLLECTION, userId));
};

// ============ INIT DEFAULT SETTINGS ============

export const initDefaultSettings = async () => {
  const settings = await getSettings();
  
  // Only init if no settings exist
  if (settings.length === 0) {
    const defaultSettings = [
      { category: 'STATUS_COLOR', key: 'YENI', value: '#3b82f6', label: 'Yeni Takip' },
      { category: 'STATUS_COLOR', key: 'TEBLIG', value: '#eab308', label: 'Tebligat Aşamasında' },
      { category: 'STATUS_COLOR', key: 'KESIN', value: '#ef4444', label: 'Kesinleşti' },
      { category: 'STATUS_COLOR', key: 'HACIZ', value: '#a855f7', label: 'Haciz İşlemi' },
      { category: 'STATUS_COLOR', key: 'ODEME', value: '#22c55e', label: 'Ödeme Sözü' },
      { category: 'STATUS_COLOR', key: 'KAPALI', value: '#64748b', label: 'Dosya Kapalı' },
    ];
    
    for (const setting of defaultSettings) {
      await addSetting(setting);
    }
    
    return defaultSettings;
  }
  
  return settings;
};
